<template>
  <div
      class="partner-payments-card bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden mt-6 w-full max-w-full min-w-0"
      dir="rtl"
  >
    <header class="px-3 sm:px-4 py-4 bg-gray-900/50 border-b border-gray-700 min-w-0">
      <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
        <div class="min-w-0 overflow-hidden">
          <h3 class="text-sm font-bold text-gray-100 truncate">
            {{ title }}
          </h3>

          <p class="text-[11px] text-gray-400 mt-1 truncate">
            {{ subtitle }}
          </p>

          <div
              v-if="total > 0"
              class="grid grid-cols-2 sm:flex sm:flex-wrap items-stretch gap-2 mt-3 min-w-0"
          >
            <div class="rounded-xl bg-gray-900/70 border border-gray-700 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-gray-500 truncate">تعداد پرداخت‌ها</p>
              <p class="text-xs font-bold text-gray-200 mt-1 truncate" dir="ltr">
                {{ formatNumber(total) }}
              </p>
            </div>

            <div class="rounded-xl bg-blue-600/10 border border-blue-500/20 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-blue-300/80 truncate">مبلغ همین صفحه</p>
              <p class="text-xs font-bold text-blue-400 mt-1 truncate" dir="ltr">
                {{ formatMoney(pageTotalAmount) }}
              </p>
            </div>

            <div class="rounded-xl bg-emerald-600/10 border border-emerald-500/20 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-emerald-300/80 truncate">تأیید شده</p>
              <p class="text-xs font-bold text-emerald-400 mt-1 truncate" dir="ltr">
                {{ formatNumber(confirmedCount) }}
              </p>
            </div>

            <div class="rounded-xl bg-amber-600/10 border border-amber-500/20 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-amber-300/80 truncate">در انتظار</p>
              <p class="text-xs font-bold text-amber-400 mt-1 truncate" dir="ltr">
                {{ formatNumber(unconfirmedCount) }}
              </p>
            </div>
          </div>
        </div>

        <button
            @click="fetchData"
            :disabled="loading"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-200 hover:bg-gray-700 transition disabled:opacity-50 shrink-0"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          بروزرسانی
        </button>
      </div>
    </header>

    <!-- Filters -->
    <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
      <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 min-w-0">
        <!-- Payment Type -->
        <div class="col-span-1 min-w-0">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            نوع پرداخت
          </label>

          <select
              v-model="selectedPaymentType"
              @change="onFilterChange"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          >
            <option
                v-for="type in paymentTypeOptions"
                :key="type.value"
                :value="type.value"
            >
              {{ type.label }}
            </option>
          </select>
        </div>

        <!-- Confirm Status -->
        <div class="col-span-1 min-w-0">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            وضعیت تأیید
          </label>

          <select
              v-model="selectedConfirmStatus"
              @change="onFilterChange"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          >
            <option
                v-for="status in confirmStatusOptions"
                :key="status.value"
                :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
        </div>

        <!-- Period -->
        <div class="col-span-1 min-w-0">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            بازه زمانی
          </label>

          <select
              v-model="selectedPeriod"
              @change="onPeriodChange"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          >
            <option
                v-for="option in periodOptions"
                :key="option.value"
                :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Start Date -->
        <div class="col-span-1 min-w-0" v-if="selectedPeriod === 'custom'">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            از تاریخ
          </label>

          <input
              type="date"
              v-model="customStartDate"
              @change="applyCustomDate"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <!-- End Date -->
        <div class="col-span-1 min-w-0" v-if="selectedPeriod === 'custom'">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            تا تاریخ
          </label>

          <input
              type="date"
              v-model="customEndDate"
              @change="applyCustomDate"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <!-- Page size -->
        <div class="col-span-2 lg:col-span-1 min-w-0">
          <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">
            تعداد در صفحه
          </label>

          <select
              v-model.number="pageSize"
              @change="changePageSize"
              class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
          >
            <option :value="20">20 پرداخت</option>
            <option :value="50">50 پرداخت</option>
            <option :value="100">100 پرداخت</option>
          </select>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 min-w-0">
        <div class="flex flex-wrap items-center gap-2 min-w-0 overflow-hidden">
          <span
              class="max-w-full min-w-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-[10px] sm:text-[11px] text-gray-300 overflow-hidden"
          >
            <i class="fas fa-filter text-gray-500 shrink-0"></i>
            <span class="truncate min-w-0">{{ activeFilterLabel }}</span>
          </span>

          <span
              v-if="partnerId"
              class="max-w-full min-w-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-600/15 border border-indigo-500/30 text-[10px] sm:text-[11px] text-indigo-300 overflow-hidden"
          >
            <i class="fas fa-user-tag shrink-0"></i>
            <span class="truncate">partner_id: {{ partnerId }}</span>
          </span>
        </div>

        <button
            v-if="customStartDate || customEndDate || selectedPeriod === 'custom'"
            @click="clearCustomDate"
            class="w-full sm:w-auto px-3 py-1.5 rounded-xl bg-gray-800 border border-gray-600 text-[11px] text-gray-300 hover:bg-gray-700 transition shrink-0"
        >
          پاک کردن تاریخ دستی
        </button>
      </div>
    </section>

    <!-- Loading -->
    <div v-if="loading" class="p-10 text-center">
      <i class="fas fa-circle-notch fa-spin text-indigo-400 text-2xl"></i>
      <p class="text-gray-400 mt-3 text-sm">
        در حال دریافت پرداخت‌ها...
      </p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="p-10 text-center">
      <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
      <p class="text-red-400 mt-3 text-sm">
        {{ error }}
      </p>

      <button
          @click="fetchData"
          class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-white text-xs transition"
      >
        تلاش مجدد
      </button>
    </div>

    <template v-else>
      <!-- Mobile Cards -->
      <div class="md:hidden p-3 space-y-3 w-full max-w-full min-w-0 overflow-hidden">
        <article
            v-for="payment in normalizedPayments"
            :key="payment.rowKey"
            class="rounded-2xl bg-gray-900/45 border border-gray-700/80 p-3 shadow-sm w-full max-w-full min-w-0 overflow-hidden"
        >
          <div class="flex items-start gap-3 min-w-0">
            <a
                v-if="payment.picture"
                :href="payment.picture"
                target="_blank"
                class="w-14 h-14 rounded-2xl bg-gray-950 border border-gray-700 overflow-hidden flex items-center justify-center shrink-0"
                title="مشاهده رسید"
            >
              <img :src="payment.picture" alt="payment receipt" class="w-full h-full object-cover" />
            </a>

            <div
                v-else
                class="w-14 h-14 rounded-2xl bg-gray-950 border border-gray-700 flex items-center justify-center text-gray-500 shrink-0"
            >
              <i class="fas fa-receipt"></i>
            </div>

            <div class="min-w-0 flex-1 overflow-hidden">
              <div class="flex flex-wrap items-center gap-1.5 min-w-0">
                <span
                    :class="paymentBadgeClass(payment.paymentType)"
                    class="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border"
                >
                  <i :class="payment.paymentType === 'cash' ? 'fas fa-money-bill-wave' : 'fas fa-money-check-alt'"></i>
                  {{ paymentTypeLabel(payment.paymentType) }}
                </span>

                <span
                    :class="confirmBadgeClass(payment.isConfirmed)"
                    class="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border"
                >
                  <i :class="payment.isConfirmed ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
                  {{ payment.isConfirmed ? 'تأیید شده' : 'در انتظار' }}
                </span>
              </div>

              <p class="text-xs font-bold text-gray-100 mt-2 truncate">
                {{ payment.title }}
              </p>

              <p class="text-[10px] text-gray-500 mt-1 truncate">
                {{ payment.note || 'بدون توضیحات' }}
              </p>

              <p v-if="payment.trackingCode" class="text-[10px] text-gray-400 mt-1 truncate" dir="ltr">
                کد پیگیری: {{ payment.trackingCode }}
              </p>
            </div>

            <p class="text-xs font-bold text-blue-400 shrink-0" dir="ltr">
              {{ formatMoney(payment.amountToman) }}
            </p>
          </div>

          <div class="grid grid-cols-2 gap-2 mt-3 min-w-0">
            <div class="rounded-xl bg-gray-800/70 border border-gray-700 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-gray-500 truncate">مبلغ اصلی</p>
              <p class="text-xs font-bold text-gray-200 mt-1 truncate" dir="ltr">
                {{ formatNumber(payment.amount) }} {{ payment.currencyTitle }}
              </p>
            </div>

            <div class="rounded-xl bg-gray-800/70 border border-gray-700 px-3 py-2 min-w-0 overflow-hidden">
              <p class="text-[10px] text-gray-500 truncate">نرخ تبدیل</p>
              <p class="text-xs font-bold text-gray-200 mt-1 truncate" dir="ltr">
                {{ formatNumber(payment.exchangeRateToToman) }}
              </p>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 text-[11px] min-w-0">
            <div class="min-w-0 overflow-hidden">
              <p class="text-gray-500 truncate">تاریخ ثبت</p>
              <p class="text-gray-300 mt-0.5 truncate">
                {{ formatDate(payment.date) }}
              </p>
            </div>

            <div class="min-w-0 overflow-hidden">
              <p class="text-gray-500 truncate">Partner</p>
              <p class="text-gray-300 mt-0.5 truncate" dir="ltr">
                {{ payment.partnerId ? `#${payment.partnerId}` : '-' }}
              </p>
            </div>

            <div v-if="payment.paymentType === 'check'" class="min-w-0 overflow-hidden">
              <p class="text-gray-500 truncate">سررسید</p>
              <p
                  class="mt-0.5 truncate"
                  :class="payment.isPendingCheck ? 'text-orange-400' : 'text-gray-300'"
              >
                {{ formatDateOnly(payment.dueDate) }}
              </p>
            </div>

            <div v-if="payment.paymentType === 'check'" class="min-w-0 overflow-hidden">
              <p class="text-gray-500 truncate">وضعیت چک</p>
              <p
                  class="mt-0.5 truncate"
                  :class="payment.isPendingCheck ? 'text-orange-400' : 'text-emerald-400'"
              >
                {{ payment.isPendingCheck ? 'سررسیدنشده' : 'سررسیدشده' }}
              </p>
            </div>
          </div>

          <div class="mt-3 pt-3 border-t border-gray-700/70 flex items-center justify-between gap-2">
            <a
                v-if="payment.picture"
                :href="payment.picture"
                target="_blank"
                class="text-[10px] text-blue-400 hover:text-blue-300"
            >
              مشاهده رسید
            </a>
            <span v-else class="text-[10px] text-gray-600">بدون رسید</span>

            <button
                v-if="!payment.isConfirmed"
                @click="openConfirmModal(payment)"
                class="px-3 py-1.5 rounded-xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 text-[10px] hover:bg-emerald-600/30 transition"
            >
              تأیید پرداخت
            </button>
          </div>
        </article>

        <div
            v-if="normalizedPayments.length === 0"
            class="px-4 py-12 text-center rounded-2xl bg-gray-900/40 border border-gray-700 w-full max-w-full min-w-0 overflow-hidden"
        >
          <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
          <p class="text-gray-400 text-sm">
            پرداختی برای نمایش وجود ندارد
          </p>
          <p class="text-gray-500 text-xs mt-1">
            فیلترها یا بازه زمانی را تغییر بده.
          </p>
        </div>
      </div>

      <!-- Desktop Table -->
      <div class="hidden md:block w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden table-scroll-safe">
        <table class="w-full min-w-[1180px] text-sm">
          <thead class="bg-gray-900/70 border-b border-gray-700">
          <tr>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">رسید</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">نوع</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تأیید</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">مبلغ تومان</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">مبلغ اصلی</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">ارز</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">کد پیگیری</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تاریخ ثبت</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">سررسید</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">وضعیت چک</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">توضیحات</th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">عملیات</th>
          </tr>
          </thead>

          <tbody class="divide-y divide-gray-700/50">
          <tr
              v-for="payment in normalizedPayments"
              :key="payment.rowKey"
              class="hover:bg-gray-700/30 transition"
          >
            <td class="px-4 py-3 text-xs">
              <a
                  v-if="payment.picture"
                  :href="payment.picture"
                  target="_blank"
                  class="w-10 h-10 rounded-xl bg-gray-950 border border-gray-700 overflow-hidden flex items-center justify-center"
                  title="مشاهده رسید"
              >
                <img :src="payment.picture" alt="payment receipt" class="w-full h-full object-cover" />
              </a>

              <div
                  v-else
                  class="w-10 h-10 rounded-xl bg-gray-950 border border-gray-700 flex items-center justify-center text-gray-500"
              >
                <i class="fas fa-receipt"></i>
              </div>
            </td>

            <td class="px-4 py-3 text-xs">
              <span
                  :class="paymentBadgeClass(payment.paymentType)"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
              >
                <i :class="payment.paymentType === 'cash' ? 'fas fa-money-bill-wave' : 'fas fa-money-check-alt'"></i>
                {{ paymentTypeLabel(payment.paymentType) }}
              </span>
            </td>

            <td class="px-4 py-3 text-xs whitespace-nowrap">
              <span
                  :class="confirmBadgeClass(payment.isConfirmed)"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
              >
                <i :class="payment.isConfirmed ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
                {{ payment.isConfirmed ? 'تأیید شده' : 'در انتظار' }}
              </span>
            </td>

            <td class="px-4 py-3 text-xs text-blue-400 font-bold whitespace-nowrap" dir="ltr">
              {{ formatMoney(payment.amountToman) }}
            </td>

            <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap" dir="ltr">
              {{ formatNumber(payment.amount) }}
            </td>

            <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap">
              <div>{{ payment.currencyTitle }}</div>
              <div class="text-[10px] text-gray-500 mt-1" dir="ltr">
                {{ payment.currencyCode }}
              </div>
            </td>

            <td class="px-4 py-3 text-xs text-gray-300 max-w-[140px]">
              <span class="block truncate" dir="ltr">
                {{ payment.trackingCode || '-' }}
              </span>
            </td>

            <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
              {{ formatDate(payment.date) }}
            </td>

            <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
              <span v-if="payment.paymentType === 'check'">
                {{ formatDateOnly(payment.dueDate) }}
              </span>
              <span v-else>-</span>
            </td>

            <td class="px-4 py-3 text-xs whitespace-nowrap">
              <span
                  v-if="payment.paymentType === 'check'"
                  :class="payment.isPendingCheck ? 'text-orange-400' : 'text-emerald-400'"
              >
                {{ payment.isPendingCheck ? 'سررسیدنشده' : 'سررسیدشده' }}
              </span>
              <span v-else class="text-gray-500">
                نقدی
              </span>
            </td>

            <td class="px-4 py-3 text-xs text-gray-300 max-w-[220px]">
              <span class="block truncate">{{ payment.note || '-' }}</span>
            </td>

            <td class="px-4 py-3 text-xs whitespace-nowrap">
              <button
                  v-if="!payment.isConfirmed"
                  @click="openConfirmModal(payment)"
                  class="px-3 py-1.5 rounded-xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 text-[11px] hover:bg-emerald-600/30 transition"
              >
                تأیید
              </button>

              <span v-else class="text-[11px] text-gray-500">
                انجام شده
              </span>
            </td>
          </tr>

          <tr v-if="normalizedPayments.length === 0">
            <td colspan="12" class="px-4 py-12 text-center">
              <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
              <p class="text-gray-400 text-sm">
                پرداختی برای نمایش وجود ندارد
              </p>
              <p class="text-gray-500 text-xs mt-1">
                فیلترها یا بازه زمانی را تغییر بده.
              </p>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Pagination -->
    <footer
        v-if="!loading && !error && total > 0"
        class="px-3 sm:px-4 py-4 bg-gray-900/30 border-t border-gray-700 min-w-0 overflow-hidden"
    >
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
        <div class="text-[11px] sm:text-xs text-gray-400 text-center sm:text-right min-w-0">
          نمایش
          <b class="text-gray-200">{{ formatNumber(fromItem) }}</b>
          تا
          <b class="text-gray-200">{{ formatNumber(toItem) }}</b>
          از
          <b class="text-gray-200">{{ formatNumber(total) }}</b>
          پرداخت
        </div>

        <div class="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-2 min-w-0">
          <button
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1 || loading"
              class="px-3 py-2 rounded-xl border text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
              :class="currentPage > 1 ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-gray-900 border-gray-700 text-gray-500'"
          >
            قبلی
          </button>

          <span class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-700 text-xs text-gray-300 text-center truncate">
            {{ formatNumber(currentPage) }} / {{ formatNumber(totalPages) }}
          </span>

          <button
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage >= totalPages || loading"
              class="px-3 py-2 rounded-xl border text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
              :class="currentPage < totalPages ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-gray-900 border-gray-700 text-gray-500'"
          >
            بعدی
          </button>
        </div>
      </div>
    </footer>

    <!-- Confirm Modal -->
    <div
        v-if="confirmModalOpen"
        class="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        dir="rtl"
    >
      <div class="w-full max-w-md rounded-2xl bg-gray-900 border border-gray-700 shadow-2xl overflow-hidden">
        <header class="px-4 py-3 border-b border-gray-700 bg-gray-950/60">
          <h3 class="text-sm font-bold text-gray-100">
            تأیید پرداخت
          </h3>
          <p class="text-[11px] text-gray-400 mt-1">
            آیا از تأیید این پرداخت مطمئن هستی؟
          </p>
        </header>

        <div class="p-4 space-y-3">
          <div v-if="selectedPaymentForConfirm" class="rounded-xl bg-gray-800/70 border border-gray-700 p-3">
            <div class="flex items-center justify-between gap-3">
              <span
                  :class="paymentBadgeClass(selectedPaymentForConfirm.paymentType)"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border"
              >
                <i :class="selectedPaymentForConfirm.paymentType === 'cash' ? 'fas fa-money-bill-wave' : 'fas fa-money-check-alt'"></i>
                {{ paymentTypeLabel(selectedPaymentForConfirm.paymentType) }}
              </span>

              <span class="text-sm font-bold text-blue-400" dir="ltr">
                {{ formatMoney(selectedPaymentForConfirm.amountToman) }}
              </span>
            </div>

            <p class="text-[11px] text-gray-400 mt-3 truncate">
              {{ selectedPaymentForConfirm.note || selectedPaymentForConfirm.title }}
            </p>

            <p
                v-if="selectedPaymentForConfirm.trackingCode"
                class="text-[11px] text-gray-400 mt-1 truncate"
                dir="ltr"
            >
              کد پیگیری: {{ selectedPaymentForConfirm.trackingCode }}
            </p>
          </div>

          <p class="text-[11px] text-amber-300 bg-amber-600/10 border border-amber-500/20 rounded-xl p-3 leading-5">
            بعد از تأیید، این پرداخت در محاسبات مالی تأییدشده حساب می‌شود.
          </p>
        </div>

        <footer class="px-4 py-3 bg-gray-950/60 border-t border-gray-700 flex items-center justify-end gap-2">
          <button
              @click="closeConfirmModal"
              :disabled="confirmSubmitting"
              class="px-4 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-300 hover:bg-gray-700 transition disabled:opacity-50"
          >
            انصراف
          </button>

          <button
              @click="submitConfirmPayment"
              :disabled="confirmSubmitting"
              class="px-4 py-2 rounded-xl bg-emerald-600 text-xs text-white hover:bg-emerald-700 transition disabled:opacity-50 inline-flex items-center gap-2"
          >
            <i v-if="confirmSubmitting" class="fas fa-circle-notch fa-spin"></i>
            تأیید پرداخت
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PartnerPaymentsList',

  props: {
    title: {
      type: String,
      default: 'لیست پرداخت‌ها'
    },
    subtitle: {
      type: String,
      default: 'نمایش پرداخت‌های نقدی و چکی بر اساس بازه زمانی'
    },
    partnerId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      loading: false,
      error: null,

      selectedPeriod: 'today',
      selectedPaymentType: 'both',
      selectedConfirmStatus: 'all',

      customStartDate: '',
      customEndDate: '',

      periodOptions: [
        { value: 'today', label: 'امروز' },
        { value: 'yesterday', label: 'دیروز' },
        { value: 'this_week', label: 'این هفته' },
        { value: 'last_week', label: 'هفته قبل' },
        { value: 'this_month', label: 'این ماه' },
        { value: 'last_month', label: 'ماه قبل' },
        { value: 'custom', label: 'بازه دستی' }
      ],

      paymentTypeOptions: [
        { value: 'both', label: 'همه پرداخت‌ها' },
        { value: 'cash', label: 'پرداخت نقدی' },
        { value: 'check', label: 'پرداخت چکی' }
      ],

      confirmStatusOptions: [
        { value: 'all', label: 'همه وضعیت‌ها' },
        { value: 'true', label: 'تأیید شده' },
        { value: 'false', label: 'در انتظار تأیید' }
      ],

      payments: [],

      currentPage: 1,
      totalPages: 1,
      total: 0,
      pageSize: 20,

      confirmModalOpen: false,
      selectedPaymentForConfirm: null,
      confirmSubmitting: false
    }
  },

  computed: {
    normalizedPayments() {
      return (this.payments || []).map((payment) => {
        const paymentType = payment.payment_type || 'unknown'
        const date = payment.date || payment.paid_at || payment.issue_date || null
        const dueDate = payment.due_date || null

        return {
          rowKey: `${paymentType}-${payment.id}`,
          id: payment.id,

          paymentType,
          paymentTypeLabel: payment.payment_type_label || this.paymentTypeLabel(paymentType),

          partnerId: payment.partner_id,

          amount: payment.amount || 0,
          amountToman: payment.amount_toman || 0,

          currencyCode: payment.currency_code || 'IRT',
          currencyTitle: payment.currency_title || 'تومان',
          exchangeRateToToman: payment.exchange_rate_to_toman || 1,

          paidAt: payment.paid_at || null,
          issueDate: payment.issue_date || null,
          dueDate,
          date,

          note: payment.note || '',
          trackingCode: payment.tracking_code || '',
          picture: payment.picture || null,
          isConfirmed: Boolean(payment.is_confirmed),

          title: this.buildPaymentTitle(paymentType, payment),
          isPendingCheck: this.isFutureDate(dueDate)
        }
      })
    },

    fromItem() {
      if (this.total === 0) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },

    toItem() {
      return Math.min(this.currentPage * this.pageSize, this.total)
    },

    pageTotalAmount() {
      return this.normalizedPayments.reduce((sum, payment) => {
        return sum + Number(payment.amountToman || 0)
      }, 0)
    },

    confirmedCount() {
      return this.normalizedPayments.filter(item => item.isConfirmed).length
    },

    unconfirmedCount() {
      return this.normalizedPayments.filter(item => !item.isConfirmed).length
    },

    activeFilterLabel() {
      const paymentType = this.paymentTypeOptions.find(item => item.value === this.selectedPaymentType)
      const period = this.periodOptions.find(item => item.value === this.selectedPeriod)
      const confirmStatus = this.confirmStatusOptions.find(item => item.value === this.selectedConfirmStatus)

      const paymentTypeLabel = paymentType ? paymentType.label : 'همه پرداخت‌ها'
      const periodLabel = period ? period.label : 'امروز'
      const confirmStatusLabel = confirmStatus ? confirmStatus.label : 'همه وضعیت‌ها'

      if (this.selectedPeriod === 'custom') {
        const start = this.customStartDate || 'ابتدا'
        const end = this.customEndDate || 'انتها'
        return `${paymentTypeLabel} / ${confirmStatusLabel} / ${start} تا ${end}`
      }

      return `${paymentTypeLabel} / ${confirmStatusLabel} / ${periodLabel}`
    }
  },

  watch: {
    partnerId() {
      this.currentPage = 1
      this.fetchData()
    }
  },

  methods: {
    buildPaymentTitle(paymentType, payment) {
      if (paymentType === 'cash') {
        return 'پرداخت نقدی'
      }

      if (paymentType === 'check') {
        return payment.due_date
            ? `چک / سررسید ${this.formatDateOnly(payment.due_date)}`
            : 'پرداخت چکی'
      }

      return 'پرداخت'
    },

    paymentTypeLabel(type) {
      if (type === 'cash') return 'نقدی'
      if (type === 'check') return 'چک'
      return 'نامشخص'
    },

    paymentBadgeClass(type) {
      if (type === 'cash') {
        return 'bg-cyan-600/15 text-cyan-300 border-cyan-500/30'
      }

      if (type === 'check') {
        return 'bg-indigo-600/15 text-indigo-300 border-indigo-500/30'
      }

      return 'bg-gray-600/15 text-gray-300 border-gray-500/30'
    },

    confirmBadgeClass(isConfirmed) {
      if (isConfirmed) {
        return 'bg-emerald-600/15 text-emerald-300 border-emerald-500/30'
      }

      return 'bg-amber-600/15 text-amber-300 border-amber-500/30'
    },

    isFutureDate(dateString) {
      if (!dateString) return false

      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return false

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      date.setHours(0, 0, 0, 0)

      return date > today
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'

      const number = Number(value)
      if (Number.isNaN(number)) return '۰'

      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatMoney(value) {
      return `${this.formatNumber(Math.round(Number(value || 0)))} تومان`
    },

    formatDate(dateString) {
      if (!dateString) return '-'

      const date = new Date(dateString)

      if (Number.isNaN(date.getTime())) return '-'

      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date)
    },

    formatDateOnly(dateString) {
      if (!dateString) return '-'

      const date = new Date(dateString)

      if (Number.isNaN(date.getTime())) return '-'

      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date)
    },

    formatDateForApi(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    getDateParams() {
      if (this.selectedPeriod === 'custom') {
        return {
          start_date: this.customStartDate || '',
          end_date: this.customEndDate || ''
        }
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      const startOfWeek = new Date(today)
      const jsDay = today.getDay()
      const daysSinceSaturday = jsDay === 6 ? 0 : jsDay + 1
      startOfWeek.setDate(today.getDate() - daysSinceSaturday)

      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(startOfWeek.getDate() + 6)

      const startOfLastWeek = new Date(startOfWeek)
      startOfLastWeek.setDate(startOfWeek.getDate() - 7)

      const endOfLastWeek = new Date(endOfWeek)
      endOfLastWeek.setDate(endOfWeek.getDate() - 7)

      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)

      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0)

      switch (this.selectedPeriod) {
        case 'today':
          return {
            start_date: this.formatDateForApi(today),
            end_date: this.formatDateForApi(today)
          }

        case 'yesterday':
          return {
            start_date: this.formatDateForApi(yesterday),
            end_date: this.formatDateForApi(yesterday)
          }

        case 'this_week':
          return {
            start_date: this.formatDateForApi(startOfWeek),
            end_date: this.formatDateForApi(endOfWeek)
          }

        case 'last_week':
          return {
            start_date: this.formatDateForApi(startOfLastWeek),
            end_date: this.formatDateForApi(endOfLastWeek)
          }

        case 'this_month':
          return {
            start_date: this.formatDateForApi(startOfMonth),
            end_date: this.formatDateForApi(endOfMonth)
          }

        case 'last_month':
          return {
            start_date: this.formatDateForApi(startOfLastMonth),
            end_date: this.formatDateForApi(endOfLastMonth)
          }

        default:
          return {}
      }
    },

    buildEndpoint(path) {
      if (this.endpoint && typeof this.endpoint === 'function') {
        return this.endpoint(path)
      }

      return path
    },

    fetchData() {
      this.loading = true
      this.error = null

      let url = this.buildEndpoint('deliver/chart/payments')

      const params = []

      params.push(`payment_type=${encodeURIComponent(this.selectedPaymentType)}`)
      params.push(`is_confirmed=${encodeURIComponent(this.selectedConfirmStatus)}`)

      if (this.partnerId) {
        params.push(`partner_id=${encodeURIComponent(this.partnerId)}`)
      }

      const dateParams = this.getDateParams()

      if (dateParams.start_date) {
        params.push(`start_date=${encodeURIComponent(dateParams.start_date)}`)
      }

      if (dateParams.end_date) {
        params.push(`end_date=${encodeURIComponent(dateParams.end_date)}`)
      }

      params.push(`page=${encodeURIComponent(this.currentPage)}`)
      params.push(`page_size=${encodeURIComponent(this.pageSize)}`)

      url += `?${params.join('&')}`

      this.request({
        url,
        method: 'get',
        success: (response) => {
          this.processData(response)
        },
        error: (err) => {
          console.error('PartnerPaymentsList API error:', err)
          this.error = 'خطا در دریافت پرداخت‌ها'
          this.loading = false
        }
      })
    },

    processData(data) {
      try {
        const results = Array.isArray(data.results) ? data.results : []

        this.payments = results
        this.total = Number(data.count || 0)
        this.totalPages = Math.max(1, Math.ceil(this.total / this.pageSize))

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages
        }

        this.loading = false
      } catch (err) {
        console.error('PartnerPaymentsList process error:', err)
        this.error = 'خطا در پردازش پرداخت‌ها'
        this.loading = false
      }
    },

    onFilterChange() {
      this.currentPage = 1
      this.fetchData()
    },

    onPeriodChange() {
      if (this.selectedPeriod !== 'custom') {
        this.customStartDate = ''
        this.customEndDate = ''
      }

      this.currentPage = 1
      this.fetchData()
    },

    applyCustomDate() {
      if (this.customStartDate || this.customEndDate) {
        this.selectedPeriod = 'custom'
        this.currentPage = 1
        this.fetchData()
      }
    },

    clearCustomDate() {
      this.customStartDate = ''
      this.customEndDate = ''
      this.selectedPeriod = 'today'
      this.currentPage = 1
      this.fetchData()
    },

    goToPage(page) {
      if (page < 1) return
      if (page > this.totalPages) return
      if (page === this.currentPage) return

      this.currentPage = page
      this.fetchData()
    },

    changePageSize() {
      this.currentPage = 1
      this.fetchData()
    },

    openConfirmModal(payment) {
      this.selectedPaymentForConfirm = payment
      this.confirmModalOpen = true
    },

    closeConfirmModal() {
      if (this.confirmSubmitting) return

      this.confirmModalOpen = false
      this.selectedPaymentForConfirm = null
    },

    submitConfirmPayment() {
      if (!this.selectedPaymentForConfirm) return

      this.confirmSubmitting = true

      /*
        اینجا بعداً API تأیید پرداخت وصل شود.

        مثال احتمالی:
        const payment = this.selectedPaymentForConfirm
        const url = this.buildEndpoint(`deliver/chart/payments/${payment.paymentType}/${payment.id}/confirm`)

        this.request({
          url,
          method: 'post',
          success: () => {
            this.confirmSubmitting = false
            this.closeConfirmModal()
            this.fetchData()
          },
          error: () => {
            this.confirmSubmitting = false
          }
        })
      */

      this.confirmSubmitting = false
      this.closeConfirmModal()
    }
  },

  mounted() {
    this.fetchData()
  }
}
</script>

<style scoped>
.partner-payments-card,
.partner-payments-card * {
  box-sizing: border-box;
}

.partner-payments-card {
  contain: layout paint;
}

.break-safe {
  overflow-wrap: anywhere;
  word-break: break-word;
}

input[type="date"],
select,
button {
  max-width: 100%;
  min-width: 0;
}

img {
  max-width: 100%;
}

table {
  border-collapse: collapse;
}

.table-scroll-safe {
  scrollbar-width: thin;
}

.truncate {
  min-width: 0;
}

.min-w-0 {
  min-width: 0 !important;
}

@media (max-width: 767px) {
  .partner-payments-card {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }
}
</style>